import { lazy, Suspense } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'

import {
  LOGIN_PATH,
  RedirectIfAuthenticated,
  RequireAuth,
  RequirePermission,
} from '../auth/protected-route'
import { usePermissions } from '../auth/use-permissions'
import { LoginPage } from '../features/auth/login-page'
import { DashboardPage } from '../features/dashboard/dashboard-page'
import { ModulePlaceholderPage } from '../features/placeholder/module-page'
import { NotFoundPage } from '../features/placeholder/not-found-page'
import {
  ALERTS_PATH,
  ALERT_PREFERENCES_PATH,
  ALERT_RULES_PATH,
  ALERT_RULE_EDIT_PATH,
  ALERT_RULE_NEW_PATH,
} from '../features/alerts/routes'
import { DIAGNOSTICS_PATH, VEHICLE_DIAGNOSTICS_PATH } from '../features/diagnostics/routes'
import {
  DRIVERS_PATH,
  DRIVER_DETAIL_PATH,
  IMMOBILIZER_PATH,
  UNIDENTIFIED_TRIPS_PATH,
  VEHICLE_IMMOBILIZER_PATH,
} from '../features/drivers/routes'
import { DRIVING_PATH, DRIVER_SCORE_PATH, SCORING_WEIGHTS_PATH } from '../features/driving/routes'
import {
  FUEL_PATH,
  FUEL_PRICES_PATH,
  FUEL_TRANSACTIONS_PATH,
  VEHICLE_FUEL_PATH,
} from '../features/fuel/routes'
import {
  ROUTES_PATH,
  ROUTE_ADHERENCE_PATH,
  ROUTE_EXECUTION_PATH,
  ROUTE_PLAN_EDIT_PATH,
  ROUTE_PLAN_NEW_PATH,
  ROUTE_PLAN_PATH,
} from '../features/routes/routes'
import {
  ADMIN_API_KEYS_PATH,
  ADMIN_AUDIT_PATH,
  ADMIN_BRANDING_PATH,
  ADMIN_DEVICES_PATH,
  ADMIN_DEVICE_CONSOLE_PATH,
  ADMIN_IMPORT_PATH,
  ADMIN_PATH,
  ADMIN_USERS_PATH,
  ADMIN_WEBHOOKS_PATH,
  ADMIN_WORKING_HOURS_PATH,
} from '../features/admin/routes'
import { ANALYTICS_PATH } from '../features/dashboard/routes'
import {
  MAINTENANCE_PATH,
  PAPERS_PATH,
  VEHICLE_MAINTENANCE_PATH,
} from '../features/maintenance/routes'
import { PREDICTIONS_PATH } from '../features/predictions/routes'
import { HARDWARE_PATH } from '../features/hardware/routes'
import { COLD_CHAIN_PATH } from '../features/cold-chain/routes'
import { ORDERS_PATH } from '../features/orders/routes'
import { TRAILERS_PATH } from '../features/trailers/routes'
import { INSPECTIONS_PATH, MY_INSPECTION_PATH } from '../features/inspections/routes'
import { STOCK_PATH } from '../features/stock/routes'
import { TYRES_PATH } from '../features/tyres/routes'
import { REPORTS_PATH, REPORT_SCHEDULES_PATH } from '../features/reports/routes'
import { FLEET_REPORT_PATH } from '../features/fleet-report/routes'
import { PLATFORM_PATH } from '../features/platform/routes'
import {
  TRANSPORT_PATH,
  WORKSITES_PATH,
  WORKSITE_DETAIL_PATH,
  WORKSITE_NEW_PATH,
  WORKSITE_REPORT_PATH,
  WORKSITE_SETTINGS_PATH,
  WORKSITE_STATEMENT_PATH,
} from '../features/worksites/routes'
import { MILEAGE_REPORT_PATH, TRIPS_PATH, TRIP_REPLAY_PATH } from '../features/trips/routes'
import { VEHICLE_DETAIL_PATH } from '../features/vehicles/routes'
import {
  POI_PATH,
  ZONES_PATH,
  ZONE_EDIT_PATH,
  ZONE_NEW_PATH,
  ZONE_VISITS_PATH,
} from '../features/zones/routes'
import { VehicleDetailPage } from '../features/vehicles/vehicle-detail-page'
import { VehiclesPage } from '../features/vehicles/vehicles-page'
import { AppShell } from '../layout/app-shell'
import { defaultRouteFor, NAV_ITEMS } from '../layout/nav-items'

/**
 * Split out of the entry chunk: MapLibre is ~1.45 MB of WebGL renderer that a driver
 * signing in to check a job never loads. The library is already a separate chunk from
 * its own dynamic import; this keeps the feature's code out of the first paint too.
 */
const MapPage = lazy(async () => ({ default: (await import('../features/map')).MapPage }))

/**
 * Same reasoning for the trips module: the replay screen draws its track with
 * MapLibre, so registering it statically would put the renderer back into the entry
 * chunk that the split above exists to keep it out of.
 */
const TripsHistoryPage = lazy(async () => ({
  default: (await import('../features/trips')).TripsHistoryPage,
}))
const TripReplayPage = lazy(async () => ({
  default: (await import('../features/trips')).TripReplayPage,
}))
const MileageReportPage = lazy(async () => ({
  default: (await import('../features/trips')).MileageReportPage,
}))

/**
 * Diagnostics is lazy for a different reason: nobody but an installer opens it, and
 * it drags in the timeline chart for the battery trend. A fleet manager checking a
 * truck should not pay for it on first paint.
 */
const FleetCapabilityPage = lazy(async () => ({
  default: (await import('../features/diagnostics')).FleetCapabilityPage,
}))
const VehicleDiagnosticsPage = lazy(async () => ({
  default: (await import('../features/diagnostics')).VehicleDiagnosticsPage,
}))

/**
 * And the fuel module, for the plainest reason of the three: importing its barrel
 * REGISTERS the FR/EN fuel catalog as a side effect (see `features/fuel/i18n.ts`), so
 * a static import would put 473 lines of catalog per language into the entry chunk of
 * every user who never opens a fuel report — including the driver the split above
 * exists for.
 */
const FleetFuelPage = lazy(async () => ({
  default: (await import('../features/fuel')).FleetFuelPage,
}))
const VehicleFuelPage = lazy(async () => ({
  default: (await import('../features/fuel')).VehicleFuelPage,
}))
const FuelTransactionsPage = lazy(async () => ({
  default: (await import('../features/fuel')).FuelTransactionsPage,
}))
const FuelPricesPage = lazy(async () => ({
  default: (await import('../features/fuel')).FuelPricesPage,
}))

/**
 * Conduite, for both reasons the fuel split gives and one of its own: importing either
 * Phase 6 barrel REGISTERS its FR/EN catalog as a side effect, and the behaviour heat
 * map pulls in MapLibre. A driver signing in to check a job pays for neither.
 */
const LeaderboardPage = lazy(async () => ({
  default: (await import('../features/driving')).LeaderboardPage,
}))
const DriverScorePage = lazy(async () => ({
  default: (await import('../features/driving')).DriverScorePage,
}))
const ScoringWeightsPage = lazy(async () => ({
  default: (await import('../features/driving')).ScoringWeightsPage,
}))
const DriversPage = lazy(async () => ({
  default: (await import('../features/drivers')).DriversPage,
}))
const DriverDetailPage = lazy(async () => ({
  default: (await import('../features/drivers')).DriverDetailPage,
}))
const UnidentifiedTripsPage = lazy(async () => ({
  default: (await import('../features/drivers')).UnidentifiedTripsPage,
}))
const ImmobilizerVehiclesPage = lazy(async () => ({
  default: (await import('../features/drivers')).ImmobilizerVehiclesPage,
}))
const VehicleImmobilizerPage = lazy(async () => ({
  default: (await import('../features/drivers')).VehicleImmobilizerPage,
}))

/**
 * Zones is lazy for the reason the map and trips splits exist, in its strongest form:
 * EVERY screen in the module reaches `zone-map.tsx`, which dynamically imports
 * MapLibre. A static import here would pull the module's code and its 216-key FR/EN
 * catalog into the entry chunk of every user who never opens a zone — and would put
 * the renderer back into the static closure the other three splits exist to keep it
 * out of. `features/zones/bundling.test.ts` guards the half of that property that
 * lives inside the module; this wrapper is the other half.
 */
const ZonesPage = lazy(async () => ({ default: (await import('../features/zones')).ZonesPage }))
const ZoneFormPage = lazy(async () => ({
  default: (await import('../features/zones')).ZoneFormPage,
}))
const VisitReportPage = lazy(async () => ({
  default: (await import('../features/zones')).VisitReportPage,
}))
const PoiPage = lazy(async () => ({ default: (await import('../features/zones')).PoiPage }))

/**
 * Alerts: importing the barrel registers its FR/EN catalog as a side effect, and the
 * rule builder reaches `features/zones` for the geofence picker — so a static import
 * would drag MapLibre in behind it. Same split, same reasons.
 */
const AlertCentrePage = lazy(async () => ({
  default: (await import('../features/alerts')).AlertCentrePage,
}))
const AlertRulesPage = lazy(async () => ({
  default: (await import('../features/alerts')).AlertRulesPage,
}))
const RuleBuilderPage = lazy(async () => ({
  default: (await import('../features/alerts')).RuleBuilderPage,
}))
const NotificationPreferencesPage = lazy(async () => ({
  default: (await import('../features/alerts')).NotificationPreferencesPage,
}))

/**
 * Route planning: the adherence report draws its two polylines with MapLibre, and
 * importing this barrel registers its 208-key FR/EN catalog as a side effect. Same
 * split, same reasons as the seven above it.
 */
const PlansPage = lazy(async () => ({ default: (await import('../features/routes')).PlansPage }))
const PlanBuilderPage = lazy(async () => ({
  default: (await import('../features/routes')).PlanBuilderPage,
}))
const PlanDetailPage = lazy(async () => ({
  default: (await import('../features/routes')).PlanDetailPage,
}))
const LiveAdherencePage = lazy(async () => ({
  default: (await import('../features/routes')).LiveAdherencePage,
}))
const AdherenceReportPage = lazy(async () => ({
  default: (await import('../features/routes')).AdherenceReportPage,
}))

/**
 * Maintenance and chantiers: importing either barrel REGISTERS its FR/EN catalogue as
 * a side effect, and `features/worksites/site-map.tsx` reaches MapLibre. Same split,
 * same reasons as the eight above.
 *
 * `DashboardPage` above stays STATIC on purpose — it is the landing screen for every
 * role holding `dashboards:read`, and a spinner in front of the first paint buys
 * nothing. `features/dashboard/bundling.test.ts` guards the consequence: that module
 * must reach no other feature barrel.
 */
const MaintenanceBoardPage = lazy(async () => ({
  default: (await import('../features/maintenance')).MaintenanceBoardPage,
}))
const PapersPage = lazy(async () => ({
  default: (await import('../features/maintenance')).PapersPage,
}))
const VehicleMaintenancePage = lazy(async () => ({
  default: (await import('../features/maintenance')).VehicleMaintenancePage,
}))
const HardwarePage = lazy(async () => ({
  default: (await import('../features/hardware')).HardwarePage,
}))

const StockPage = lazy(async () => ({
  default: (await import('../features/stock')).StockPage,
}))

const SelfPage = lazy(async () => ({
  default: (await import('../features/self')).SelfPage,
}))

const InspectionsPage = lazy(async () => ({
  default: (await import('../features/inspections')).InspectionsPage,
}))

const OrdersPage = lazy(async () => ({
  default: (await import('../features/orders')).OrdersPage,
}))

const TrailersPage = lazy(async () => ({
  default: (await import('../features/trailers')).TrailersPage,
}))

const ColdChainPage = lazy(async () => ({
  default: (await import('../features/cold-chain')).ColdChainPage,
}))

const MyInspectionPage = lazy(async () => ({
  default: (await import('../features/inspections')).MyInspectionPage,
}))

const TyresPage = lazy(async () => ({
  default: (await import('../features/tyres')).TyresPage,
}))
const PredictionsPage = lazy(async () => ({
  default: (await import('../features/predictions')).PredictionsPage,
}))
const WorksitesPage = lazy(async () => ({
  default: (await import('../features/worksites')).WorksitesPage,
}))
const SiteDashboardPage = lazy(async () => ({
  default: (await import('../features/worksites')).SiteDashboardPage,
}))
const WorksiteSettingsPage = lazy(async () => ({
  default: (await import('../features/worksites')).WorksiteSettingsPage,
}))
const WorksiteReportPage = lazy(async () => ({
  default: (await import('../features/worksites')).WorksiteReportPage,
}))
const StatementPage = lazy(async () => ({
  default: (await import('../features/worksites')).StatementPage,
}))
const TransportPage = lazy(async () => ({
  default: (await import('../features/worksites')).TransportPage,
}))

/**
 * Analytics and the report library: the reports barrel registers a ~150-key catalogue
 * per language on import, and the analytics screen carries the heat map. Neither is
 * on the path a driver or a dispatcher takes.
 */
const AnalyticsPage = lazy(async () => ({
  default: (await import('../features/dashboard')).AnalyticsPage,
}))
const ReportsLibraryPage = lazy(async () => ({
  default: (await import('../features/reports')).ReportsLibraryPage,
}))
const PlatformPage = lazy(async () => ({
  default: (await import('../features/platform')).PlatformPage,
}))
const FleetReportPage = lazy(async () => ({
  default: (await import('../features/fleet-report')).FleetReportPage,
}))
const SchedulesPage = lazy(async () => ({
  default: (await import('../features/reports')).SchedulesPage,
}))

/**
 * Administration: importing this barrel REGISTERS its ~400-key FR/EN catalogue as a
 * side effect, and seven of its nine screens stop at tenant_admin. Same split, same
 * reasons as the eleven above it. It reaches no map — `features/admin/bundling.test.ts`
 * fails if that ever changes.
 */
const AdminPage = lazy(async () => ({ default: (await import('../features/admin')).AdminPage }))
const AdminDevicesPage = lazy(async () => ({
  default: (await import('../features/admin')).AdminDevicesPage,
}))
const DeviceConsolePage = lazy(async () => ({
  default: (await import('../features/admin')).DeviceConsolePage,
}))
const BulkImportPage = lazy(async () => ({
  default: (await import('../features/admin')).BulkImportPage,
}))
const BrandingPage = lazy(async () => ({
  default: (await import('../features/admin')).BrandingPage,
}))
const WorkingHoursPage = lazy(async () => ({
  default: (await import('../features/admin')).WorkingHoursPage,
}))
const ApiKeysPage = lazy(async () => ({
  default: (await import('../features/admin')).ApiKeysPage,
}))
const WebhooksPage = lazy(async () => ({
  default: (await import('../features/admin')).WebhooksPage,
}))
const AuditPage = lazy(async () => ({ default: (await import('../features/admin')).AuditPage }))
const AdminUsersPage = lazy(async () => ({
  default: (await import('../features/admin')).UsersPage,
}))

/** "/" is per-user: a driver has no dashboard permission and must not land on it. */
function DefaultRedirect() {
  const { roles } = usePermissions()
  return <Navigate to={defaultRouteFor(roles)} replace />
}

function LoginRoute() {
  const { roles } = usePermissions()
  return (
    <RedirectIfAuthenticated to={defaultRouteFor(roles)}>
      <LoginPage />
    </RedirectIfAuthenticated>
  )
}

// Dashboard, vehicles and the live map have real screens; every other module is
// routed to its placeholder so the nav, the permission gate and the deep link are
// already the ones a later phase will keep.
/**
 * Exported for `real-screens.test.ts`, which is the whole point of it being a named
 * constant. Four times now a module has shipped complete, tested and unreachable
 * because its key was missing from this set — `map`, `admin`, `stock`, `myInspection`.
 * Every component suite passed each time, because component suites mount the page
 * directly and never go through the router.
 */
export const REAL_SCREENS = new Set([
  'dashboard',
  'map',
  'trips',
  'fuel',
  'driving',
  'alerts',
  'geofences',
  'routes',
  'maintenance',
  // Papiers & vidange: the fleet's papers, oil changes and driving licences.
  'papers',
  'tyres',
  'predictions',
  'hardware',
  // Without this, /admin/stock gets BOTH the real route and a placeholder route on
  // the identical path — the real one only wins because it is declared first, which is
  // not a guarantee worth relying on.
  'stock',
  'self',
  'inspections',
  'coldChain',
  'trailers',
  'orders',
  // The driver's own walk-around. Without this the placeholder route ALSO matches
  // /my-inspection, and the real screen wins only by being declared first — which is
  // not a guarantee React Router makes.
  'myInspection',
  'reports',
  // Rapport flotte, under /reports/fleet.
  'fleetReport',
  // Every organisation's vehicles, for the platform operator.
  'platform',
  'worksites',
  // Without this the placeholder route below also matches /admin and wins, and nine
  // built, tested administration screens stay unreachable behind a "coming later" card.
  'admin',
])
const PLACEHOLDER_ITEMS = NAV_ITEMS.filter((item) => !REAL_SCREENS.has(item.key))

export function AppRoutes() {
  return (
    <Routes>
      <Route path={LOGIN_PATH} element={<LoginRoute />} />

      <Route element={<RequireAuth />}>
        <Route element={<AppShell />}>
          <Route index element={<DefaultRedirect />} />

          <Route
            path="/dashboard"
            element={
              <RequirePermission anyOf={['dashboards:read']}>
                <DashboardPage />
              </RequirePermission>
            }
          />

          <Route
            path="/vehicles"
            element={
              <RequirePermission anyOf={['vehicles:read']}>
                <VehiclesPage />
              </RequirePermission>
            }
          />

          <Route
            path={VEHICLE_DETAIL_PATH}
            element={
              <RequirePermission anyOf={['vehicles:read']}>
                <VehicleDetailPage />
              </RequirePermission>
            }
          />

          {/* `/diagnostics/vehicles/:id` is declared before `/diagnostics` only for
              readability — React Router ranks the deeper pattern regardless. Both are
              gated on `telemetry:read_raw`, the same permission the capability and
              raw-data endpoints require: this is a hardware-debugging surface. */}
          <Route
            path={VEHICLE_DIAGNOSTICS_PATH}
            element={
              <RequirePermission anyOf={['telemetry:read_raw']}>
                <Suspense fallback={null}>
                  <VehicleDiagnosticsPage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path={DIAGNOSTICS_PATH}
            element={
              <RequirePermission anyOf={['telemetry:read_raw']}>
                <Suspense fallback={null}>
                  <FleetCapabilityPage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path="/map"
            element={
              <RequirePermission anyOf={['live:read']}>
                <Suspense fallback={null}>
                  <MapPage />
                </Suspense>
              </RequirePermission>
            }
          />

          {/* `/trips/mileage` is declared before `/trips` only for readability —
              React Router ranks a static segment above a dynamic one regardless. */}
          <Route
            path={MILEAGE_REPORT_PATH}
            element={
              <RequirePermission anyOf={['reports:read', 'trips:read']}>
                <Suspense fallback={null}>
                  <MileageReportPage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path={TRIP_REPLAY_PATH}
            element={
              <RequirePermission anyOf={['trips:read']}>
                <Suspense fallback={null}>
                  <TripReplayPage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path={TRIPS_PATH}
            element={
              <RequirePermission anyOf={['trips:read']}>
                <Suspense fallback={null}>
                  <TripsHistoryPage />
                </Suspense>
              </RequirePermission>
            }
          />

          {/*
            The static path first only for readability — React Router ranks a literal
            segment above a dynamic one regardless of declaration order.
          */}
          <Route
            path={FUEL_TRANSACTIONS_PATH}
            element={
              <RequirePermission anyOf={['fuel:read']}>
                <Suspense fallback={null}>
                  <FuelTransactionsPage />
                </Suspense>
              </RequirePermission>
            }
          />

          {/* Guarded on `fuel:read` like its siblings even though writing needs
              `fuel:write`: a manager who can read a cost per kilometre should be able
              to see the price it was computed from, and the form refuses them
              server-side. Hiding the page would leave the number unexplainable. */}
          <Route
            path={FUEL_PRICES_PATH}
            element={
              <RequirePermission anyOf={['fuel:read']}>
                <Suspense fallback={null}>
                  <FuelPricesPage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path={VEHICLE_FUEL_PATH}
            element={
              <RequirePermission anyOf={['fuel:read']}>
                <Suspense fallback={null}>
                  <VehicleFuelPage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path={FUEL_PATH}
            element={
              <RequirePermission anyOf={['fuel:read']}>
                <Suspense fallback={null}>
                  <FleetFuelPage />
                </Suspense>
              </RequirePermission>
            }
          />

          {/* Static segments first only for readability — React Router ranks them above
              their dynamic siblings regardless of declaration order. */}
          <Route
            path={SCORING_WEIGHTS_PATH}
            element={
              <RequirePermission anyOf={['scoring:configure']}>
                <Suspense fallback={null}>
                  <ScoringWeightsPage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path={DRIVER_SCORE_PATH}
            element={
              <RequirePermission anyOf={['scoring:read']}>
                <Suspense fallback={null}>
                  <DriverScorePage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path={DRIVING_PATH}
            element={
              <RequirePermission anyOf={['scoring:read']}>
                <Suspense fallback={null}>
                  <LeaderboardPage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path={UNIDENTIFIED_TRIPS_PATH}
            element={
              <RequirePermission anyOf={['drivers:read']}>
                <Suspense fallback={null}>
                  <UnidentifiedTripsPage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path={DRIVER_DETAIL_PATH}
            element={
              <RequirePermission anyOf={['drivers:read']}>
                <Suspense fallback={null}>
                  <DriverDetailPage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path={DRIVERS_PATH}
            element={
              <RequirePermission anyOf={['drivers:read']}>
                <Suspense fallback={null}>
                  <DriversPage />
                </Suspense>
              </RequirePermission>
            }
          />

          {/* `devices:immobilizer` stops at tenant_admin. Do not widen it here. */}
          <Route
            path={VEHICLE_IMMOBILIZER_PATH}
            element={
              <RequirePermission anyOf={['devices:immobilizer']}>
                <Suspense fallback={null}>
                  <VehicleImmobilizerPage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path={IMMOBILIZER_PATH}
            element={
              <RequirePermission anyOf={['devices:immobilizer']}>
                <Suspense fallback={null}>
                  <ImmobilizerVehiclesPage />
                </Suspense>
              </RequirePermission>
            }
          />

          {/* Static segments before the dynamic `:ruleId`, as elsewhere in this file,
              for readability only — React Router ranks a literal segment higher
              regardless. `alerts:configure` stops at fleet_manager; the feed and the
              per-user preferences only need `alerts:read`, because a driver who can
              see an alert must still be able to choose how they are told about it. */}
          <Route
            path={ALERT_PREFERENCES_PATH}
            element={
              <RequirePermission anyOf={['alerts:read']}>
                <Suspense fallback={null}>
                  <NotificationPreferencesPage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path={ALERT_RULE_NEW_PATH}
            element={
              <RequirePermission anyOf={['alerts:configure']}>
                <Suspense fallback={null}>
                  <RuleBuilderPage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path={ALERT_RULE_EDIT_PATH}
            element={
              <RequirePermission anyOf={['alerts:configure']}>
                <Suspense fallback={null}>
                  <RuleBuilderPage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path={ALERT_RULES_PATH}
            element={
              <RequirePermission anyOf={['alerts:configure']}>
                <Suspense fallback={null}>
                  <AlertRulesPage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path={ALERTS_PATH}
            element={
              <RequirePermission anyOf={['alerts:read']}>
                <Suspense fallback={null}>
                  <AlertCentrePage />
                </Suspense>
              </RequirePermission>
            }
          />

          {/* `ZONE_EDIT_PATH` is gated on `geofences:read`, not `write`: a viewer may
              open a zone to read the rules that govern their vehicles, and the
              destructive controls inside are permission-gated separately. */}
          <Route
            path={ZONE_VISITS_PATH}
            element={
              <RequirePermission anyOf={['geofences:read']}>
                <Suspense fallback={null}>
                  <VisitReportPage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path={POI_PATH}
            element={
              <RequirePermission anyOf={['geofences:read']}>
                <Suspense fallback={null}>
                  <PoiPage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path={ZONE_NEW_PATH}
            element={
              <RequirePermission anyOf={['geofences:write']}>
                <Suspense fallback={null}>
                  <ZoneFormPage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path={ZONE_EDIT_PATH}
            element={
              <RequirePermission anyOf={['geofences:read']}>
                <Suspense fallback={null}>
                  <ZoneFormPage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path={ZONES_PATH}
            element={
              <RequirePermission anyOf={['geofences:read']}>
                <Suspense fallback={null}>
                  <ZonesPage />
                </Suspense>
              </RequirePermission>
            }
          />

          {/* Static segments before their dynamic siblings for readability only —
              React Router ranks them regardless. The adherence report is gated on
              `reports:read` OR `routes:read` because it is served by
              `AdherenceReportController` while the live view beside it comes from
              `RouteExecutionsController`: a user holding either one gets the screen
              they can actually fill. */}
          <Route
            path={ROUTE_PLAN_NEW_PATH}
            element={
              <RequirePermission anyOf={['routes:write']}>
                <Suspense fallback={null}>
                  <PlanBuilderPage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path={ROUTE_ADHERENCE_PATH}
            element={
              <RequirePermission anyOf={['reports:read', 'routes:read']}>
                <Suspense fallback={null}>
                  <AdherenceReportPage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path={ROUTE_EXECUTION_PATH}
            element={
              <RequirePermission anyOf={['routes:read']}>
                <Suspense fallback={null}>
                  <LiveAdherencePage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path={ROUTE_PLAN_EDIT_PATH}
            element={
              <RequirePermission anyOf={['routes:write']}>
                <Suspense fallback={null}>
                  <PlanBuilderPage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path={ROUTE_PLAN_PATH}
            element={
              <RequirePermission anyOf={['routes:read']}>
                <Suspense fallback={null}>
                  <PlanDetailPage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path={ROUTES_PATH}
            element={
              <RequirePermission anyOf={['routes:read']}>
                <Suspense fallback={null}>
                  <PlansPage />
                </Suspense>
              </RequirePermission>
            }
          />

          {/* The board is the root and the vehicle view hangs off it: a workshop
              arrives with "what needs doing this week", not with a registration. */}
          <Route
            path={PAPERS_PATH}
            element={
              <RequirePermission anyOf={['maintenance:read']}>
                <Suspense fallback={null}>
                  <PapersPage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path={VEHICLE_MAINTENANCE_PATH}
            element={
              <RequirePermission anyOf={['maintenance:read']}>
                <Suspense fallback={null}>
                  <VehicleMaintenancePage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path={MAINTENANCE_PATH}
            element={
              <RequirePermission anyOf={['maintenance:read']}>
                <Suspense fallback={null}>
                  <MaintenanceBoardPage />
                </Suspense>
              </RequirePermission>
            }
          />

          {/* Suivi de pneumatique reuses `maintenance:read` rather than a permission of
              its own: the person who books a vidange is the person who orders tyres, and
              a new permission would have to be granted to every existing fleet_manager
              before the screen did anything. */}
          {/* Requires BOTH `scoring:read` and `fuel:read`: the screen fuses driving
              behaviour with fuel spend, and a role holding one of the two would read a
              page whose empty half looks like missing data rather than missing
              permission. The server asks for both, and so does the route. */}
          <Route
            path={PREDICTIONS_PATH}
            element={
              <RequirePermission anyOf={['scoring:read']}>
                <Suspense fallback={null}>
                  <PredictionsPage />
                </Suspense>
              </RequirePermission>
            }
          />

          {/* Reuses `devices:read` rather than minting `hardware:*`: the person who
              registers a tracker is the person who records where it was fitted and who
              went out to it, and a permission of its own would have to be granted to
              every existing fleet_manager before the screen did anything.

              The read gate is deliberately no wider than the device list a viewer can
              already see. A technician who cannot open the dossier is a technician who
              dismantles a dashboard to rediscover where the box is. */}
          {/* The workshop's board. `maintenance:read` for the reason the controller
              gives — the person who books a vidange reads the walk-arounds. */}
          <Route
            path={INSPECTIONS_PATH}
            element={
              <RequirePermission anyOf={['maintenance:read']}>
                <Suspense fallback={null}>
                  <InspectionsPage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path={ORDERS_PATH}
            element={
              <RequirePermission anyOf={['routes:read']}>
                <Suspense fallback={null}>
                  <OrdersPage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path={TRAILERS_PATH}
            element={
              <RequirePermission anyOf={['vehicles:read']}>
                <Suspense fallback={null}>
                  <TrailersPage />
                </Suspense>
              </RequirePermission>
            }
          />

          {/* Shares `maintenance:read` with the rest of the atelier surfaces rather than
              minting `coldchain:*` — a new permission appears for nobody until somebody
              grants it by hand to every fleet_manager that already exists. */}
          <Route
            path={COLD_CHAIN_PATH}
            element={
              <RequirePermission anyOf={['maintenance:read']}>
                <Suspense fallback={null}>
                  <ColdChainPage />
                </Suspense>
              </RequirePermission>
            }
          />

          {/* The driver's walk-around. `self:inspect` is the ONE write the driver role
              holds — they report a fault, a mechanic closes it. */}
          <Route
            path={MY_INSPECTION_PATH}
            element={
              <RequirePermission anyOf={['self:inspect']}>
                <Suspense fallback={null}>
                  <MyInspectionPage />
                </Suspense>
              </RequirePermission>
            }
          />

          {/* The only route a `driver` account can reach. `self:read` is that role's
              entire permission set, and no other role holds it — a fleet_manager gets
              403 here and uses /drivers/:id instead, so one answer has one path. */}
          <Route
            path="/my-trips"
            element={
              <RequirePermission anyOf={['self:read']}>
                <Suspense fallback={null}>
                  <SelfPage />
                </Suspense>
              </RequirePermission>
            }
          />

          {/* The warehouse is the OPERATOR's balance sheet — what each tracker cost and
              how long capital has been sitting in a cupboard. `platform:manage_stock`
              is granted to super_admin alone, and the route guard mirrors the
              controller rather than trusting it: a customer must not reach this screen
              even to see it render empty. */}
          <Route
            path={STOCK_PATH}
            element={
              <RequirePermission anyOf={['platform:manage_stock']}>
                <Suspense fallback={null}>
                  <StockPage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path={HARDWARE_PATH}
            element={
              <RequirePermission anyOf={['devices:read']}>
                <Suspense fallback={null}>
                  <HardwarePage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path={TYRES_PATH}
            element={
              <RequirePermission anyOf={['maintenance:read']}>
                <Suspense fallback={null}>
                  <TyresPage />
                </Suspense>
              </RequirePermission>
            }
          />

          {/* `worksites:billing` is deliberately NOT a route guard. The statement and
              cost endpoints are gated on it server-side; guarding the route as well
              would hide the page from a site manager who reached it from a link, with
              no explanation of why. `/worksites/transport` and `/worksites/new` are
              literal segments and are declared before `:worksiteId` for readability —
              React Router ranks them above the dynamic sibling regardless. */}
          <Route
            path={TRANSPORT_PATH}
            element={
              <RequirePermission anyOf={['worksites:read']}>
                <Suspense fallback={null}>
                  <TransportPage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path={WORKSITE_NEW_PATH}
            element={
              <RequirePermission anyOf={['worksites:write']}>
                <Suspense fallback={null}>
                  <WorksiteSettingsPage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path={WORKSITE_SETTINGS_PATH}
            element={
              <RequirePermission anyOf={['worksites:read']}>
                <Suspense fallback={null}>
                  <WorksiteSettingsPage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path={WORKSITE_REPORT_PATH}
            element={
              <RequirePermission anyOf={['worksites:read']}>
                <Suspense fallback={null}>
                  <WorksiteReportPage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path={WORKSITE_STATEMENT_PATH}
            element={
              <RequirePermission anyOf={['worksites:read']}>
                <Suspense fallback={null}>
                  <StatementPage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path={WORKSITE_DETAIL_PATH}
            element={
              <RequirePermission anyOf={['worksites:read']}>
                <Suspense fallback={null}>
                  <SiteDashboardPage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path={WORKSITES_PATH}
            element={
              <RequirePermission anyOf={['worksites:read']}>
                <Suspense fallback={null}>
                  <WorksitesPage />
                </Suspense>
              </RequirePermission>
            }
          />

          {/* `/analytics` is a CONTRACT WITH THE API, not a local choice:
              `dashboards.tiles.ts` builds the utilisation tile with
              `withQuery('/analytics', …)`, so renaming it here produces a tile that
              navigates to the not-found page. */}
          <Route
            path={ANALYTICS_PATH}
            element={
              <RequirePermission anyOf={['dashboards:read']}>
                <Suspense fallback={null}>
                  <AnalyticsPage />
                </Suspense>
              </RequirePermission>
            }
          />

          {/* Scheduling sends fleet data to an e-mail address, repeatedly, to somebody
              who may hold no RIVENA account at all — the one operation in this
              module that crosses the login boundary, and the reason it carries
              `reports:schedule` rather than `reports:read`. */}
          <Route
            path={REPORT_SCHEDULES_PATH}
            element={
              <RequirePermission anyOf={['reports:schedule']}>
                <Suspense fallback={null}>
                  <SchedulesPage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path={PLATFORM_PATH}
            element={
              <RequirePermission anyOf={['platform:manage_tenants']}>
                <Suspense fallback={null}>
                  <PlatformPage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path={FLEET_REPORT_PATH}
            element={
              <RequirePermission anyOf={['reports:read']}>
                <Suspense fallback={null}>
                  <FleetReportPage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path={REPORTS_PATH}
            element={
              <RequirePermission anyOf={['reports:read']}>
                <Suspense fallback={null}>
                  <ReportsLibraryPage />
                </Suspense>
              </RequirePermission>
            }
          />

          {/* §8.11. SEVEN DIFFERENT PERMISSIONS, and that is not tidiness —
              `ROLE_PERMISSIONS` separates them deliberately. `devices:command` reaches
              dispatcher; the three import permissions reach fleet_manager;
              `apikeys:manage`, `webhooks:manage`, `audit:read`, `branding:write` and
              `users:read` all stop at tenant_admin. Gating the module on one permission
              would either lock a dispatcher out of the console they are entitled to use,
              or hand a fleet manager the key-minting screen.

              `/admin` itself is gated on the UNION, exactly as `nav-items.ts` already
              gates the sidebar entry, and the index renders only the sections the caller
              can open. The static segments are declared before `:deviceId` for
              readability only — React Router ranks a literal segment above a dynamic
              sibling regardless. */}
          <Route
            path={ADMIN_DEVICE_CONSOLE_PATH}
            element={
              <RequirePermission anyOf={['devices:read']}>
                <Suspense fallback={null}>
                  <DeviceConsolePage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path={ADMIN_DEVICES_PATH}
            element={
              <RequirePermission anyOf={['devices:read']}>
                <Suspense fallback={null}>
                  <AdminDevicesPage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path={ADMIN_IMPORT_PATH}
            element={
              <RequirePermission anyOf={['vehicles:write', 'devices:write', 'drivers:write']}>
                <Suspense fallback={null}>
                  <BulkImportPage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path={ADMIN_WORKING_HOURS_PATH}
            element={
              <RequirePermission anyOf={['branding:write']}>
                <Suspense fallback={null}>
                  <WorkingHoursPage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path={ADMIN_BRANDING_PATH}
            element={
              <RequirePermission anyOf={['branding:write']}>
                <Suspense fallback={null}>
                  <BrandingPage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path={ADMIN_API_KEYS_PATH}
            element={
              <RequirePermission anyOf={['apikeys:manage']}>
                <Suspense fallback={null}>
                  <ApiKeysPage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path={ADMIN_WEBHOOKS_PATH}
            element={
              <RequirePermission anyOf={['webhooks:manage']}>
                <Suspense fallback={null}>
                  <WebhooksPage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path={ADMIN_AUDIT_PATH}
            element={
              <RequirePermission anyOf={['audit:read']}>
                <Suspense fallback={null}>
                  <AuditPage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path={ADMIN_USERS_PATH}
            element={
              <RequirePermission anyOf={['users:read']}>
                <Suspense fallback={null}>
                  <AdminUsersPage />
                </Suspense>
              </RequirePermission>
            }
          />

          <Route
            path={ADMIN_PATH}
            element={
              <RequirePermission
                anyOf={['users:read', 'settings:write', 'apikeys:manage', 'audit:read']}
              >
                <Suspense fallback={null}>
                  <AdminPage />
                </Suspense>
              </RequirePermission>
            }
          />

          {PLACEHOLDER_ITEMS.map((item) => (
            <Route
              key={item.key}
              path={item.path}
              element={
                <RequirePermission anyOf={item.permissions}>
                  <ModulePlaceholderPage navKey={item.key} />
                </RequirePermission>
              }
            />
          ))}

          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Route>
    </Routes>
  )
}
